import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatCard, SectionCard, StatusBadge, Empty } from "@/components/shared";
import { inr, num } from "@/lib/format";
import { Megaphone, Plus, X, TrendingUp, Users, Target } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

export default function Campaigns() {
  const [camps, setCamps] = useState([]);
  const [events, setEvents] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/campaigns").then((r) => setCamps(r.data));
    api.get("/campaigns/funnel").then((r) => setFunnel(r.data));
    api.get("/reports/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Campaigns & Events" subtitle="Marketing programs with live ROI funnel (Priority-2, wired)." testid="campaigns-page"
        actions={<button data-testid="new-campaign-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 text-white font-semibold px-4 py-2 rounded-lg copper-glow" style={{ background: "var(--copper)" }}><Plus size={16} /> New Campaign</button>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <StatCard label="Total Spend" value={funnel ? inr(funnel.budget) : "—"} accent="#7C3AED" icon={Megaphone} testid="camp-budget" />
        <StatCard label="Leads Reached" value={funnel ? num(funnel.leads) : "—"} accent="#2563EB" icon={Users} testid="camp-leads" />
        <StatCard label="Converted" value={funnel ? num(funnel.converted) : "—"} sub={funnel ? inr(funnel.est_revenue) + " est." : ""} accent="#059669" icon={Target} testid="camp-converted" />
        <StatCard label="Program ROI" value={funnel ? `${funnel.roi}x` : "—"} sub="revenue / spend" accent="var(--copper)" icon={TrendingUp} testid="camp-roi" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="ROI Funnel" className="lg:col-span-2" testid="roi-funnel">
          {funnel && (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={funnel.funnel} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="stage" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} width={80} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} fill="#C86D23" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
        <SectionCard title="Events">
          {events.length === 0 ? <Empty /> : events.map((e) => (
            <div key={e.id} className="py-2.5 border-b border-slate-50 last:border-0"><p className="text-sm font-medium text-slate-800">{e.name}</p><p className="text-xs text-slate-400">{e.city} · {e.attendees} attendees</p></div>
          ))}
        </SectionCard>
      </div>

      <SectionCard title="Campaigns" className="!p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100"><th className="px-5 py-3">Campaign</th><th className="px-5 py-3">Channel</th><th className="px-5 py-3">Budget</th><th className="px-5 py-3">Leads</th><th className="px-5 py-3">Converted</th><th className="px-5 py-3">ROI</th><th className="px-5 py-3">Status</th></tr></thead>
            <tbody>
              {camps.map((c) => (
                <tr key={c.id} className="border-b border-slate-50">
                  <td className="px-5 py-3"><p className="font-medium text-slate-800">{c.name}</p><p className="text-xs text-slate-400">{c.description}</p></td>
                  <td className="px-5 py-3 text-slate-600">{c.channel || "Multi"}</td>
                  <td className="px-5 py-3 font-mono">{inr(c.budget)}</td>
                  <td className="px-5 py-3 font-mono">{num(c.leads)}</td>
                  <td className="px-5 py-3 font-mono">{num(c.converted ?? Math.round((c.leads || 0) * 0.25))}</td>
                  <td className="px-5 py-3 font-mono font-semibold text-[var(--copper)]">{c.roi}x</td>
                  <td className="px-5 py-3"><StatusBadge status={c.status === "active" ? "active" : c.status === "completed" ? "approved" : "pending"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {camps.length === 0 && <Empty />}
        </div>
      </SectionCard>

      {open && <CampaignDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); toast.success("Campaign created"); }} />}
    </div>
  );
}

function CampaignDialog({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", description: "", budget: 500000, channel: "Multi-channel" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    try { await api.post("/campaigns", { ...f, budget: Number(f.budget) }); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="campaign-dialog">
        <div className="flex justify-between items-center mb-4"><h3 className="font-bold text-slate-900">New Campaign</h3><button onClick={onClose} className="text-slate-400"><X size={18} /></button></div>
        <div className="space-y-3">
          <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Campaign name" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="campaign-name" />
          <input value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder="Budget ₹" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            <select value={f.channel} onChange={(e) => set("channel", e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">{["Multi-channel", "Email", "SMS", "WhatsApp", "Events"].map((c) => <option key={c}>{c}</option>)}</select>
          </div>
          <button onClick={submit} data-testid="campaign-submit" className="w-full text-white font-semibold py-2.5 rounded-lg copper-glow" style={{ background: "var(--copper)" }}>Create Campaign</button>
        </div>
      </div>
    </div>
  );
}
